import { Tooltip } from '@frontend-platform/ui-lib';
import { useEffect, useState } from 'react';
import { DevShellDisabledControl } from './dev-shell-controls';

type BadgeConfig = {
  label: string;
  dotClass: string;
  switchCommand: string;
};

function resolveBadge(mode: string, projectName: string): BadgeConfig {
  if (mode === 'qa') {
    return {
      label: 'QA',
      dotClass: 'bg-amber-500',
      switchCommand: `pnpm nx dev ${projectName}`,
    };
  }
  return {
    label: mode === 'development' ? 'DEV' : mode.toUpperCase(),
    dotClass: 'bg-sky-500',
    switchCommand: `pnpm nx dev ${projectName} --configuration=qa`,
  };
}

export interface DevShellBackendBadgeProps {
  projectName: string;
  /**
   * The Vite mode the app was started with, read by the app from its own
   * `VITE_<APP>_LOCAL_ENV` define. Vite reads the proxy env files once at
   * startup, so the badge is an indicator, not a switch. Without it the badge
   * renders disabled.
   */
  localEnv?: string;
  /** A cheap same-origin GET through the dev proxy, probed once on mount. */
  healthCheckPath?: string;
  /** Skip the probe while the app serves mocked APIs. */
  mockActive?: boolean;
}

export type BackendReachability = 'checking' | 'up' | 'down' | 'unproxied' | 'mock' | 'unwired';

export async function probeBackend(path: string): Promise<BackendReachability> {
  try {
    const response = await fetch(path, { method: 'GET', cache: 'no-store' });
    if (response.ok || response.status === 401 || response.status === 403) return 'up';
    return response.status === 404 ? 'unproxied' : 'down';
  } catch {
    return 'down';
  }
}

const REACHABILITY: Record<BackendReachability, { dot: string; text: string }> = {
  checking: { dot: 'bg-steel-blue-45', text: 'Checking the backend.' },
  up: { dot: 'bg-green-active', text: 'Backend reachable.' },
  down: {
    dot: 'bg-red-500',
    text: 'Backend unreachable: is the dev proxy target up and the VPN on?',
  },
  unproxied: { dot: 'bg-amber-500', text: 'Health check path is not proxied (404).' },
  mock: { dot: 'bg-violet-500', text: 'Reachability not checked while Mock is ON.' },
  unwired: {
    dot: 'bg-steel-blue-45',
    text: 'Reachability not wired: pass config.healthCheckPath (a cheap proxied GET).',
  },
};

function useBackendReachability(path: string | undefined, mockActive: boolean) {
  const [probed, setProbed] = useState<BackendReachability>('checking');

  useEffect(() => {
    if (!path || mockActive) return;
    let cancelled = false;
    setProbed('checking');
    probeBackend(path).then((next) => {
      if (!cancelled) setProbed(next);
    });
    return () => {
      cancelled = true;
    };
  }, [path, mockActive]);

  if (!path) return 'unwired';
  return mockActive ? 'mock' : probed;
}

export function DevShellBackendBadge({
  projectName,
  localEnv,
  healthCheckPath,
  mockActive = false,
}: DevShellBackendBadgeProps) {
  const reachability = useBackendReachability(healthCheckPath, mockActive);

  if (localEnv === undefined) {
    return (
      <DevShellDisabledControl
        hint={`Backend badge not wired: add a qa Nx configuration and a VITE_<APP>_LOCAL_ENV define, then pass it as config.localEnv (see apps/training). Today ${projectName} has one backend.`}
      >
        <span className="block size-1.5 rounded-full bg-steel-blue-45" aria-hidden />
        Backend
      </DevShellDisabledControl>
    );
  }
  const { label, dotClass, switchCommand } = resolveBadge(localEnv, projectName);

  return (
    <Tooltip
      title={`Local backend: ${label}. To switch, restart with: ${switchCommand}. ${REACHABILITY[reachability].text}`}
    >
      <div
        data-reachability={reachability}
        className="flex h-9 items-center gap-1.5 rounded-md border border-steel-blue-45 bg-white px-2.5 font-medium text-xs"
      >
        <span className={`block size-1.5 rounded-full ${dotClass}`} aria-hidden />
        <span className="text-steel-light">Backend</span>
        <span className="text-steel-ultradark">{label}</span>
        <span
          className={`block size-1.5 rounded-full ${REACHABILITY[reachability].dot}`}
          aria-hidden
        />
      </div>
    </Tooltip>
  );
}
